import React, { useState, useEffect } from 'react';
import { Upload, FileText, DollarSign, TrendingUp, Calendar, Building, Plus, RefreshCw } from 'lucide-react';
import axios from 'axios';
import Disclaimer from '../components/Disclaimer';
import { getTaxYearOptions } from '../utils/taxYears';
import { toast } from 'react-hot-toast';

const TaxableAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    accountId: '',
    institutionType: '',
    statementDate: '',
    statementPeriodStart: '',
    statementPeriodEnd: ''
  });
  const [transactionData, setTransactionData] = useState({
    accountId: '',
    transaction_date: '',
    transaction_type: 'interest',
    amount: '',
    description: '',
    is_qualified_dividend: false,
    is_short_term_gain: false
  });

  const institutionTypes = [
    { value: 'marcus', label: 'Marcus (Goldman Sachs)' },
    { value: 'schwab', label: 'Schwab (Regular Account)' },
    { value: 'schwab-roth-ira', label: 'Schwab Roth IRA' },
    { value: 'schwab-ira', label: 'Schwab IRA' },
    { value: 'usaa', label: 'USAA Main Account' },
    { value: 'usaa-credit', label: 'USAA Credit Card' }
  ];

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accountsRes, transactionsRes, summaryRes, statementsRes] = await Promise.all([
        axios.get('/api/accounts'),
        axios.get(`/api/taxable-accounts/transactions?taxYear=${selectedYear}`),
        axios.get(`/api/taxable-accounts/summary?taxYear=${selectedYear}`),
        axios.get('/api/taxable-accounts/statements')
      ]);

      setAccounts(accountsRes.data.data);
      setTransactions(transactionsRes.data.data);
      setSummary(summaryRes.data.data);
      setStatements(statementsRes.data.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!uploadData.accountId || !uploadData.statementDate) {
      toast.error('Please select an account and statement date');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('statement', file);
      formData.append('accountId', uploadData.accountId);
      formData.append('institutionType', uploadData.institutionType);
      formData.append('statementDate', uploadData.statementDate);
      formData.append('statementPeriodStart', uploadData.statementPeriodStart);
      formData.append('statementPeriodEnd', uploadData.statementPeriodEnd);

      const response = await axios.post('/api/taxable-accounts/upload-statement', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Statement processed successfully! Added ${response.data.data.transactionsAdded} transactions.`);
      setShowUploadModal(false);
      setUploadData({
        accountId: '',
        institutionType: '',
        statementDate: '',
        statementPeriodStart: '',
        statementPeriodEnd: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error uploading statement:', error);
      toast.error(error.response?.data?.error || 'Failed to upload statement');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = async () => {
    if (!transactionData.accountId || !transactionData.transaction_date || !transactionData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/taxable-accounts/add-transaction', {
        accountId: transactionData.accountId,
        transactionData: {
          ...transactionData,
          tax_year: selectedYear,
          amount: parseFloat(transactionData.amount)
        }
      });

      toast.success('Transaction added successfully!');
      setShowAddTransactionModal(false);
      setTransactionData({
        accountId: '',
        transaction_date: '',
        transaction_type: 'interest',
        amount: '',
        description: '',
        is_qualified_dividend: false,
        is_short_term_gain: false
      });
      fetchData();
    } catch (error) {
      console.error('Error adding transaction:', error);
      toast.error(error.response?.data?.error || 'Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`/api/taxable-accounts/transactions/${transactionId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
      });

      toast.success('Transaction deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error(error.response?.data?.error || 'Failed to delete transaction');
    } finally {
      setLoading(false);
    }
  };

  const recalculateSummary = async () => {
    setLoading(true);
    try {
      await axios.post('/api/taxable-accounts/recalculate-summary', {
        taxYear: selectedYear
      });
      toast.success('Summary recalculated successfully!');
      fetchData();
    } catch (error) {
      console.error('Error recalculating summary:', error);
      toast.error('Failed to recalculate summary');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Statements</h1>
          <p className="text-gray-600">Upload and track account statements for taxable and tax-deferred accounts</p>
        </div>
        <Disclaimer variant="tax" />

        {/* Year Selector */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Tax Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2"
            >
              {getTaxYearOptions().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              onClick={recalculateSummary}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              Recalculate
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <Upload className="w-4 h-4" />
            Upload Statement
          </button>
          <button
            onClick={() => setShowAddTransactionModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Transaction
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <DollarSign className="w-8 h-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Interest Income</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.total_interest_income)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <TrendingUp className="w-8 h-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Dividend Income</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.total_dividend_income)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <TrendingUp className="w-8 h-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Capital Gains</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.net_capital_gains)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <DollarSign className="w-8 h-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Taxable</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.total_taxable_income)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Transactions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.transaction_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.account_name} ({transaction.institution})
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        transaction.transaction_type === 'interest' ? 'bg-green-100 text-green-800' :
                        transaction.transaction_type === 'dividend' ? 'bg-blue-100 text-blue-800' :
                        transaction.transaction_type === 'capital_gain' ? 'bg-purple-100 text-purple-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {transaction.transaction_type.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.is_qualified_dividend && 'Qualified Dividend'}
                      {transaction.is_short_term_gain && 'Short-term'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => handleDeleteTransaction(transaction.id)}
                        className="text-red-600 hover:text-red-900 font-medium"
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Account Statement</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Institution Type</label>
                    <select
                      value={uploadData.institutionType}
                      onChange={(e) => setUploadData({...uploadData, institutionType: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Select Institution Type</option>
                      {institutionTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account (Optional)</label>
                    <select
                      value={uploadData.accountId}
                      onChange={(e) => setUploadData({...uploadData, accountId: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Select Account (Optional)</option>
                      {accounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.name} ({account.institution})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Leave empty to create new account from statement</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Statement Date</label>
                    <input
                      type="date"
                      value={uploadData.statementDate}
                      onChange={(e) => setUploadData({...uploadData, statementDate: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Statement File</label>
                    <input
                      type="file"
                      accept=".txt,.pdf"
                      onChange={handleFileUpload}
                      className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Transaction Modal */}
        {showAddTransactionModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add Transaction</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account</label>
                    <select
                      value={transactionData.accountId}
                      onChange={(e) => setTransactionData({...transactionData, accountId: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Select Account</option>
                      {accounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.name} ({account.institution})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Transaction Date</label>
                    <input
                      type="date"
                      value={transactionData.transaction_date}
                      onChange={(e) => setTransactionData({...transactionData, transaction_date: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <select
                      value={transactionData.transaction_type}
                      onChange={(e) => setTransactionData({...transactionData, transaction_type: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="interest">Interest</option>
                      <option value="dividend">Dividend</option>
                      <option value="capital_gain">Capital Gain</option>
                      <option value="capital_loss">Capital Loss</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={transactionData.amount}
                      onChange={(e) => setTransactionData({...transactionData, amount: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <input
                      type="text"
                      value={transactionData.description}
                      onChange={(e) => setTransactionData({...transactionData, description: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  {transactionData.transaction_type === 'dividend' && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={transactionData.is_qualified_dividend}
                        onChange={(e) => setTransactionData({...transactionData, is_qualified_dividend: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">Qualified Dividend</label>
                    </div>
                  )}
                  {(transactionData.transaction_type === 'capital_gain' || transactionData.transaction_type === 'capital_loss') && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={transactionData.is_short_term_gain}
                        onChange={(e) => setTransactionData({...transactionData, is_short_term_gain: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">Short-term</label>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowAddTransactionModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddTransaction}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    Add Transaction
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxableAccounts;
